import {
  atelierCentAus,
  atelierPreisebenen,
  atelierProzentAus,
  atelierWert,
  centAlsText,
  type AtelierEingaben,
  type AtelierFehler,
  type AtelierKopf,
  type AtelierModul,
  type AtelierZeile,
  type Atelierpreise,
} from "@repo/shared";

const euro = (cent: number) => (cent / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const zahl = (wert: number) => wert.toLocaleString("de-DE");
const einheit = (z: AtelierZeile) => (z.art === "prozent" ? "%" : z.einheit);

const ERKLAERUNG = "EK privat = EK der Liste + Mindermengenaufschlag. ERP-EK und UVP rechnen auf den EK privat.";

function Spalten() {
  return (
    <thead>
      <tr>
        <th>Ausführung</th>
        <th>Einheit</th>
        <th className="num">EK</th>
        <th className="num">EK privat</th>
        <th className="num">ERP-EK</th>
        <th className="num">UVP netto</th>
        <th className="num">UVP brutto</th>
      </tr>
    </thead>
  );
}

/** Aufschläge aus den Formularwerten; null, solange einer nicht lesbar ist. */
function kopfAus(werte: AtelierEingaben, erpAufschlag: string): AtelierKopf | null {
  const mindermengeProzent = atelierProzentAus(werte.mindermengeProzent);
  const erpAufschlagProzent = atelierProzentAus(erpAufschlag);
  const uvpAufschlagProzent = atelierProzentAus(werte.uvpAufschlagProzent);
  return mindermengeProzent === null || erpAufschlagProzent === null || uvpAufschlagProzent === null
    ? null
    : { mindermengeProzent, erpAufschlagProzent, uvpAufschlagProzent };
}

/** Lesesicht: alle Ebenen als Text, aus dem Modul gebaut. */
export function AtelierpreiseAnsicht({
  modul,
  preise,
  erpAufschlagProzent,
}: {
  modul: AtelierModul;
  preise: Atelierpreise | null;
  erpAufschlagProzent: number;
}) {
  if (!preise) {
    return (
      <section className="atelierpreise">
        <h2>Preisliste {modul.name}</h2>
        <p className="muted">Noch keine Preisliste – über „Bearbeiten“ eintragen.</p>
      </section>
    );
  }
  const kopf: AtelierKopf = {
    mindermengeProzent: preise.mindermengeProzent,
    erpAufschlagProzent,
    uvpAufschlagProzent: preise.uvpAufschlagProzent,
  };
  return (
    <section className="atelierpreise">
      <h2>Preisliste {modul.name}</h2>
      <dl className="info">
        <dt>Preislistenstand</dt>
        <dd>{new Date(`${preise.stand}T00:00:00`).toLocaleDateString("de-DE")}</dd>
        <dt>Mindermengenaufschlag</dt>
        <dd>{zahl(preise.mindermengeProzent)} %</dd>
        <dt>ERP-Aufschlag</dt>
        <dd>{zahl(erpAufschlagProzent)} %</dd>
        <dt>UVP-Aufschlag</dt>
        <dd>{zahl(preise.uvpAufschlagProzent)} %</dd>
      </dl>
      <p className="muted small">{ERKLAERUNG}</p>
      {modul.abschnitte.map((a) => (
        <fieldset key={a.titel}>
          <legend>{a.titel}</legend>
          {a.info.map((text) => (
            <p key={text} className="muted small">
              {text}
            </p>
          ))}
          <div className="tabelle-rahmen">
            <table className="table atelier-tabelle">
              <Spalten />
              <tbody>
                {a.zeilen.map((z) => {
                  const wert = atelierWert(modul, preise, z.schluessel);
                  if (z.art !== "geld") {
                    return (
                      <tr key={z.schluessel}>
                        <td>{z.label}</td>
                        <td>{einheit(z)}</td>
                        <td className="num">{z.art === "prozent" ? `+${zahl(wert)} %` : zahl(wert)}</td>
                        <td colSpan={4} className="muted small">
                          gilt für alle Ebenen
                        </td>
                      </tr>
                    );
                  }
                  const e = atelierPreisebenen(wert, kopf, preise.uvpVonHand[z.schluessel]);
                  return (
                    <tr key={z.schluessel}>
                      <td>{z.label}</td>
                      <td>{z.einheit}</td>
                      <td className="num">{euro(wert)}</td>
                      <td className="num">{euro(e.ekPrivatCent)}</td>
                      <td className="num">{euro(e.erpEkCent)}</td>
                      <td className="num">
                        {euro(e.uvpNettoCent)}
                        {e.uvpVonHand && <div className="muted small">von Hand</div>}
                      </td>
                      <td className="num">{euro(e.uvpBruttoCent)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </fieldset>
      ))}
    </section>
  );
}

/** Bearbeiten: EK und Prozentwerte je Zeile, UVP je Zeile von Hand; Ebenen rechnen live. */
export function AtelierpreiseEingabe({
  modul,
  werte,
  fehler,
  erpAufschlag,
  erpFehler,
  onChange,
  onErpAufschlag,
}: {
  modul: AtelierModul;
  werte: AtelierEingaben;
  fehler: AtelierFehler;
  /** Der ERP-Aufschlag ist der Aufschlag des Herstellers – das Formularfeld `aufschlagProzent`. */
  erpAufschlag: string;
  erpFehler?: string;
  onChange: (werte: AtelierEingaben) => void;
  onErpAufschlag: (text: string) => void;
}) {
  const kopf = kopfAus(werte, erpAufschlag);
  const setzeWert = (schluessel: string, text: string) => onChange({ ...werte, werte: { ...werte.werte, [schluessel]: text } });
  const setzeUvp = (schluessel: string, text: string) =>
    onChange({ ...werte, uvpVonHand: { ...werte.uvpVonHand, [schluessel]: text } });
  const automatisch = (schluessel: string) => {
    const uvpVonHand = { ...werte.uvpVonHand };
    delete uvpVonHand[schluessel];
    onChange({ ...werte, uvpVonHand });
  };
  const prozentFeld = (feld: "mindermengeProzent" | "uvpAufschlagProzent", label: string, hinweis: string) => (
    <label className="field">
      <span>{label}</span>
      <input inputMode="decimal" value={werte[feld]} onChange={(e) => onChange({ ...werte, [feld]: e.target.value })} />
      <small className="muted">{hinweis}</small>
      {fehler[feld] && <small className="error">{fehler[feld]}</small>}
    </label>
  );

  return (
    <section className="atelierpreise">
      <h2>Preisliste {modul.name}</h2>
      <fieldset>
        <legend>Kopf</legend>
        <label className="field">
          <span>Preislistenstand</span>
          <input type="date" value={werte.stand} onChange={(e) => onChange({ ...werte, stand: e.target.value })} />
          {fehler.stand && <small className="error">{fehler.stand}</small>}
        </label>
        {prozentFeld("mindermengeProzent", "Mindermengenaufschlag (%)", "Auf den EK der Liste – ergibt den EK privat.")}
        <label className="field">
          <span>ERP-Aufschlag (%)</span>
          <input inputMode="decimal" value={erpAufschlag} onChange={(e) => onErpAufschlag(e.target.value)} />
          <small className="muted">Auf den EK privat – ergibt den EK, den das ERP bekommt.</small>
          {erpFehler && <small className="error">{erpFehler}</small>}
        </label>
        {prozentFeld("uvpAufschlagProzent", "UVP-Aufschlag (%)", "Auf den EK privat – ergibt die UVP; je Zeile von Hand änderbar.")}
        <p className="muted small">{ERKLAERUNG}</p>
      </fieldset>
      {modul.abschnitte.map((a) => (
        <fieldset key={a.titel}>
          <legend>{a.titel}</legend>
          {a.info.map((text) => (
            <p key={text} className="muted small">
              {text}
            </p>
          ))}
          <div className="tabelle-rahmen">
            <table className="table atelier-tabelle">
              <Spalten />
              <tbody>
                {a.zeilen.map((z) => {
                  const text = werte.werte[z.schluessel] ?? "";
                  const zeilenFehler = fehler[z.schluessel];
                  if (z.art !== "geld") {
                    return (
                      <tr key={z.schluessel}>
                        <td>
                          {z.label}
                          {zeilenFehler && <small className="error">{zeilenFehler}</small>}
                        </td>
                        <td>{einheit(z)}</td>
                        <td className="num">
                          <input
                            inputMode="decimal"
                            aria-label={z.label}
                            value={text}
                            onChange={(e) => setzeWert(z.schluessel, e.target.value)}
                          />
                        </td>
                        <td colSpan={4} className="muted small">
                          gilt für alle Ebenen
                        </td>
                      </tr>
                    );
                  }
                  const ek = atelierCentAus(text);
                  const handText = werte.uvpVonHand[z.schluessel];
                  const hand = handText === undefined ? undefined : (atelierCentAus(handText) ?? undefined);
                  const e = ek !== null && kopf ? atelierPreisebenen(ek, kopf, hand) : null;
                  const uvpFehler = fehler[`uvp:${z.schluessel}`];
                  return (
                    <tr key={z.schluessel}>
                      <td>
                        {z.label}
                        {zeilenFehler && <small className="error">{zeilenFehler}</small>}
                        {uvpFehler && <small className="error">UVP: {uvpFehler}</small>}
                      </td>
                      <td>{z.einheit}</td>
                      <td className="num">
                        <input
                          inputMode="decimal"
                          aria-label={`${z.label} – EK`}
                          value={text}
                          onChange={(ev) => setzeWert(z.schluessel, ev.target.value)}
                        />
                      </td>
                      <td className="num">{e ? euro(e.ekPrivatCent) : "—"}</td>
                      <td className="num">{e ? euro(e.erpEkCent) : "—"}</td>
                      <td className="num">
                        <input
                          inputMode="decimal"
                          aria-label={`${z.label} – UVP netto`}
                          value={handText ?? (e ? centAlsText(e.uvpNettoCent) : "")}
                          onChange={(ev) => setzeUvp(z.schluessel, ev.target.value)}
                        />
                        {handText !== undefined && (
                          <button type="button" className="link" onClick={() => automatisch(z.schluessel)}>
                            automatisch
                          </button>
                        )}
                      </td>
                      <td className="num">{e ? euro(e.uvpBruttoCent) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </fieldset>
      ))}
    </section>
  );
}
